/**
 * TCodeItem Component
 * Displays a single Entitlement item with hover interactions
 */

import React from 'react';
import { Box, Icon, type IconKind, Typography } from '@saviynt/design-system';

import { type TCodeData } from '../../types';

interface TCodeItemProps {
  tCode: TCodeData;
  onClick?: (codeId: string) => void;
  className?: string;
}

/**
 * TCodeItem Component
 * Displays a single Entitlement item with hover interactions
 * @param tCode - The Entitlement data to display
 * @param onClick - Callback function when the item is clicked
 * @param className - Optional CSS class name
 * @returns JSX element
 */
function TCodeItem({ tCode, onClick, className = '' }: TCodeItemProps) {
  const handleClick = () => {
    onClick?.(tCode.id);
  };

  const isInteractive = typeof onClick === 'function';

  let iconKind: IconKind = 'AlertInfo';
  let iconColor: string | undefined;

  const severity = String(tCode.severity ?? '').toUpperCase();
  const isNoRiskSeverity =
    severity === 'NORISK' || severity === 'LOW' || severity === 'NONE';

  if (severity) {
    iconKind = isNoRiskSeverity ? 'CheckCircleOutline' : 'AlertCriticalOutline';
    iconColor = isNoRiskSeverity
      ? '--color-foreground-success-medium'
      : '--color-foreground-critical-medium';
  }

  const content = (
    <Box className='Sod-TCodeListRow-item-interaction-container'>
      <Box className='Sod-TCodeListRow-item-front-space'>
        <Icon
          kind={iconKind}
          className='Sod-TCodeListRow-item-alert-icon'
          size='small'
          color={iconColor}
        />
      </Box>
      <Box className='Sod-TCodeListRow-item-side-panel-trigger-area'>
        <Typography kind='body2' lineLimit={2}>
          {tCode.description}
        </Typography>
      </Box>
      <Box className='Sod-TCodeListRow-item-arrow'>
        <Icon
          kind='ArrowRight'
          size='small'
          className='Sod-TCodeListRow-item-arrow-icon'
        />
      </Box>
    </Box>
  );

  if (!isInteractive) {
    return (
      <div className={`Sod-TCodeListRow-item ${className || ''}`}>
        {content}
      </div>
    );
  }

  return (
    <div
      className={`Sod-TCodeListRow-item ${className || ''}`}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.stopPropagation();
          handleClick();
        }
      }}
      role='button'
      tabIndex={0}>
      {content}
    </div>
  );
}

TCodeItem.defaultProps = {
  onClick: undefined,
  className: '',
};

export default TCodeItem;
